import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  CalendarBlank, CaretLeft, CaretRight, ArrowLeft, ArrowsCounterClockwise, UserPlus,
  Clock, Users, MapPin, VideoCamera, CheckCircle, XCircle, MagnifyingGlass, Lightning,
  EnvelopeSimple, Phone, Bell, X as XIcon, Warning,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { ClosurePanel } from '../components/seller/ClosurePanel';
import { usePlatformSettings } from '../hooks/usePlatformSettings';
import { Prohibit } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const isoDateLocal = (d) => {
  // Return YYYY-MM-DD in the user's local timezone (no UTC shift).
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const SellerSlotManager = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [date, setDate] = useState(() => isoDateLocal(new Date()));
  const [data, setData] = useState({ listings: [], live_classes: [], totals: { regular_bookings: 0, live_class_participants: 0 } });
  const [loading, setLoading] = useState(true);
  const [rescheduleTarget, setRescheduleTarget] = useState(null); // booking obj
  const [manualAddTarget, setManualAddTarget] = useState(null); // { listing_id, listing_title, slot? }
  const [showClosures, setShowClosures] = useState(false);
  const platformSettings = usePlatformSettings();

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/seller/slot-manager/day`, { params: { date: d } });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load slots'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(date); }, [date, load]);

  const shiftDate = (deltaDays) => {
    const d = new Date(date + 'T00:00:00');
    d.setDate(d.getDate() + deltaDays);
    setDate(isoDateLocal(d));
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="slot-manager-page">
      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/seller')} className="p-2 rounded-lg hover:bg-slate-100 text-slate-700" data-testid="back-to-dashboard">
              <ArrowLeft size={18} />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Slot Manager</h1>
              <p className="text-xs text-slate-500">Manage buyers slot-by-slot across all listings and live sessiones.</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            {platformSettings.seller_closures !== false && (
              <Button size="sm" variant="outline" onClick={() => setShowClosures(true)}
                className="h-8 rounded-lg text-xs border-rose-200 text-rose-600 hover:bg-rose-50"
                data-testid="open-closures-btn">
                <Prohibit size={14} className="mr-1" weight="bold" /> Close Space
              </Button>
            )}
            <span className="px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 font-semibold" data-testid="stat-regular">
              {data.totals.regular_bookings} regular
            </span>
            <span className="px-2.5 py-1 rounded-full bg-violet-100 text-violet-700 font-semibold" data-testid="stat-live">
              {data.totals.live_class_participants} live-class
            </span>
          </div>
        </div>

        {/* Date picker */}
        <div className="bg-white rounded-2xl border border-slate-100 p-3 mb-4 flex items-center gap-2 justify-between flex-wrap">
          <div className="flex items-center gap-2">
            <button onClick={() => shiftDate(-1)} className="p-2 rounded-lg hover:bg-slate-100" data-testid="day-prev"><CaretLeft size={16} /></button>
            <div className="flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-2 border border-slate-200">
              <CalendarBlank size={16} className="text-slate-500" />
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="border-0 bg-transparent p-0 h-7 text-sm w-36" data-testid="slot-date-input" />
            </div>
            <button onClick={() => shiftDate(1)} className="p-2 rounded-lg hover:bg-slate-100" data-testid="day-next"><CaretRight size={16} /></button>
            <Button size="sm" variant="outline" onClick={() => setDate(isoDateLocal(new Date()))} className="ml-1 h-9 rounded-lg text-xs" data-testid="day-today">Today</Button>
          </div>
          <Button size="sm" onClick={() => load(date)} className="bg-slate-700 hover:bg-slate-800 h-9 rounded-lg text-xs" data-testid="day-refresh">Refresh</Button>
        </div>

        {loading ? (
          <div className="text-center text-slate-500 py-12">Loading…</div>
        ) : (
          <div className="space-y-4">
            {/* Regular listings */}
            {data.listings.length === 0 && data.live_classes.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center">
                <CalendarBlank size={40} className="mx-auto text-slate-300 mb-2" />
                <h3 className="text-slate-700 font-semibold">No bookings on this date</h3>
                <p className="text-xs text-slate-500 mt-1">Pick another day, or use the &quot;Add Walk-in&quot; button below to manually book a slot.</p>
              </div>
            ) : null}

            {data.listings.map(group => (
              <ListingGroup key={group.listing.listing_id} group={group}
                onMove={(booking) => setRescheduleTarget(booking)}
                onAdd={(slot) => setManualAddTarget({
                  listing_id: group.listing.listing_id,
                  listing_title: group.listing.title,
                  slot,
                })}
              />
            ))}

            {/* Live sessions */}
            {data.live_classes.length > 0 && (
              <div className="bg-white rounded-2xl border border-violet-200 overflow-hidden">
                <div className="px-4 py-3 bg-gradient-to-r from-violet-50 to-fuchsia-50 border-b border-violet-100 flex items-center gap-2">
                  <VideoCamera size={18} weight="fill" className="text-violet-600" />
                  <h3 className="text-sm font-bold text-slate-900">Live Sessions</h3>
                  <span className="text-xs text-slate-500">({data.live_classes.length})</span>
                </div>
                <div className="divide-y divide-slate-100">
                  {data.live_classes.map(cls => (
                    <LiveClassRow key={cls.class_id} cls={cls} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {showClosures && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" data-testid="closures-modal">
          <div className="w-full max-w-2xl mt-10">
            <div className="flex justify-end mb-2">
              <button onClick={() => setShowClosures(false)}
                className="bg-white rounded-full p-2 shadow hover:bg-slate-100" data-testid="close-closures-modal">
                <XIcon size={16} />
              </button>
            </div>
            <ClosurePanel onChanged={() => load(date)} />
          </div>
        </div>
      )}
      {rescheduleTarget && (
        <RescheduleModal
          booking={rescheduleTarget}
          listings={data.listings.map(g => g.listing)}
          onClose={() => setRescheduleTarget(null)}
          onDone={() => { setRescheduleTarget(null); load(date); }}
        />
      )}
      {manualAddTarget && (
        <ManualBookingModal
          target={manualAddTarget}
          defaultDate={date}
          onClose={() => setManualAddTarget(null)}
          onDone={() => { setManualAddTarget(null); load(date); }}
        />
      )}

      {/* Floating Add Walk-in — always available so seller can create a booking
          on an empty day (the modal lets them pick the listing). */}
      <button
        onClick={() => setManualAddTarget(
          data.listings[0]
            ? { listing_id: data.listings[0].listing.listing_id, listing_title: data.listings[0].listing.title }
            : { listing_id: '', listing_title: '(pick a listing in the modal)' }
        )}
        className="fixed bottom-6 right-6 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white px-4 py-3 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all flex items-center gap-2 text-sm font-semibold"
        data-testid="add-walkin-fab"
      >
        <UserPlus size={16} weight="bold" /> Add Buyer
      </button>
    </div>
  );
};

/* =================================================================================
   ListingGroup: one card per listing, showing all booked slots that day
================================================================================= */
const ListingGroup = ({ group, onMove, onAdd }) => {
  const slots = Object.values(group.slots).sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
  return (
    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden" data-testid={`listing-group-${group.listing.listing_id}`}>
      <div className="px-4 py-3 bg-gradient-to-r from-sky-50 to-blue-50 border-b border-blue-100 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <MapPin size={16} weight="fill" className="text-blue-600 shrink-0" />
          <h3 className="text-sm font-bold text-slate-900 truncate">{group.listing.title}</h3>
          <span className="text-xs text-slate-500 shrink-0">· {group.listing.category}</span>
        </div>
        <span className="text-xs text-slate-500 shrink-0">{slots.length} slot{slots.length === 1 ? '' : 's'}</span>
      </div>
      <div className="divide-y divide-slate-100">
        {slots.map(slot => (
          <div key={slot.slot_key} className="px-4 py-3" data-testid={`slot-${slot.slot_key}`}>
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
                <Clock size={13} weight="fill" className="text-slate-500" />
                {slot.start_time} – {slot.end_time}
                <span className="text-slate-400 font-normal">· {slot.bookings.length} booking{slot.bookings.length === 1 ? '' : 's'}</span>
              </div>
              <button
                onClick={() => onAdd(slot)}
                className="text-[11px] text-emerald-700 hover:text-emerald-900 font-semibold flex items-center gap-1"
                data-testid={`add-buyer-${slot.slot_key}`}
              >
                <UserPlus size={11} weight="bold" /> Add buyer to this slot
              </button>
            </div>
            <div className="space-y-1.5 ml-5">
              {slot.bookings.map(b => (
                <BookingRow key={b.booking_id} booking={b} listing={group.listing}
                  slotStartTime={slot.start_time} slotEndTime={slot.end_time}
                  onMove={onMove} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* =================================================================================
   BookingRow: single buyer line with Move action
================================================================================= */
const BookingRow = ({ booking, listing, slotStartTime, slotEndTime, onMove }) => {
  const statusColor = {
    confirmed: 'bg-emerald-100 text-emerald-700',
    active: 'bg-emerald-100 text-emerald-700',
    checked_in: 'bg-blue-100 text-blue-700',
    completed: 'bg-slate-100 text-slate-600',
    pending: 'bg-amber-100 text-amber-700',
    pending_payment: 'bg-amber-100 text-amber-700',
    pending_approval: 'bg-amber-100 text-amber-700',
  }[booking.status] || 'bg-slate-100 text-slate-600';
  return (
    <div className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors" data-testid={`booking-row-${booking.booking_id}`}>
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-300 to-slate-400 text-white flex items-center justify-center text-xs font-bold shrink-0">
        {(booking.buyer?.name || 'W').charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
          <span className="truncate">{booking.buyer?.name || 'Walk-in'}</span>
          {booking.is_manual && <span className="text-[9px] uppercase tracking-wider bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">manual</span>}
        </div>
        <div className="text-[11px] text-slate-500 truncate">
          {booking.buyer?.email || booking.buyer?.phone || '—'}
          {booking.guests && booking.guests > 1 ? ` · ${booking.guests} guests` : ''}
        </div>
      </div>
      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold ${statusColor}`}>
        {booking.status}
      </span>
      <button
        onClick={() => onMove({ ...booking, listing, start_time: slotStartTime, end_time: slotEndTime })}
        className="p-1.5 rounded-md text-slate-500 hover:text-blue-600 hover:bg-blue-100"
        title="Move slot"
        data-testid={`move-booking-${booking.booking_id}`}
      >
        <ArrowsCounterClockwise size={14} />
      </button>
    </div>
  );
};

/* =================================================================================
   LiveClassRow: read-only summary (live sessiones have their own join/leave flow)
================================================================================= */
const LiveClassRow = ({ cls }) => (
  <div className="px-4 py-3" data-testid={`live-class-${cls.class_id}`}>
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <div className="flex items-center gap-2 min-w-0">
        <Clock size={13} weight="fill" className="text-violet-500 shrink-0" />
        <span className="text-xs font-semibold text-slate-700 shrink-0">{cls.start_time} – {cls.end_time}</span>
        <span className="text-sm text-slate-900 truncate">· {cls.title}</span>
      </div>
      <div className="flex items-center gap-1.5 text-xs text-slate-500 shrink-0">
        <Users size={12} weight="fill" />
        {cls.participants.length}/{cls.capacity || '∞'}
      </div>
    </div>
    {cls.participants.length > 0 && (
      <div className="mt-2 flex flex-wrap gap-1.5 ml-5">
        {cls.participants.map(p => (
          <span key={p.user_id} className="inline-flex items-center gap-1 text-[11px] bg-violet-50 border border-violet-200 text-violet-700 px-2 py-0.5 rounded-full">
            <span className="w-4 h-4 rounded-full bg-violet-300 text-white flex items-center justify-center text-[9px] font-bold">{(p.name || 'U').charAt(0)}</span>
            {p.name}
          </span>
        ))}
      </div>
    )}
  </div>
);

/* =================================================================================
   RescheduleModal
================================================================================= */
const RescheduleModal = ({ booking, listings, onClose, onDone }) => {
  const [form, setForm] = useState({
    new_listing_id: booking.listing?.listing_id || booking.listing_id,
    new_date: '',
    new_start_time: booking.start_time || '',
    new_end_time: booking.end_time || '',
    note_to_buyer: '',
    override_capacity: false,
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.new_date || !form.new_start_time || !form.new_end_time) {
      toast.error('Pick a new date and start/end time'); return;
    }
    setBusy(true);
    try {
      const payload = {
        booking_id: booking.booking_id,
        new_listing_id: form.new_listing_id,
        new_start_date: `${form.new_date}T00:00:00Z`,
        new_end_date: `${form.new_date}T23:59:00Z`,
        new_start_time: form.new_start_time,
        new_end_time: form.new_end_time,
        note_to_buyer: form.note_to_buyer,
        override_capacity: form.override_capacity,
      };
      const { data } = await axios.post(`${API_URL}/api/seller/slot-manager/reschedule`, payload);
      toast.success(data.buyer_notified ? 'Slot moved & buyer notified' : 'Slot moved (walk-in — no notification)');
      onDone();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Reschedule failed';
      // Surface capacity-full hint
      if (err.response?.status === 409 && !form.override_capacity) {
        toast.error(msg + ' — tick "Override capacity" to force.');
      } else toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="reschedule-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ArrowsCounterClockwise size={18} className="text-blue-600" weight="fill" />
            <h3 className="font-bold text-slate-900">Move Slot</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><XIcon size={18} /></button>
        </div>

        <div className="bg-slate-50 rounded-lg p-3 mb-4 text-xs">
          <p className="text-slate-500">Buyer</p>
          <p className="font-semibold text-slate-900">{booking.buyer?.name || 'Walk-in'}</p>
          <p className="text-slate-500 mt-1.5">Current slot</p>
          <p className="font-semibold text-slate-900">{booking.listing?.title || ''} · {booking.start_time || '—'} – {booking.end_time || '—'}</p>
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-600">New listing (optional — same is default)</Label>
            <Select value={form.new_listing_id} onValueChange={(v) => setForm({ ...form, new_listing_id: v })}>
              <SelectTrigger className="h-10 rounded-xl mt-1 text-sm" data-testid="reschedule-listing-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {listings.map(l => (<SelectItem key={l.listing_id} value={l.listing_id}>{l.title}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">New date</Label>
            <Input type="date" value={form.new_date} onChange={(e) => setForm({ ...form, new_date: e.target.value })} className="h-10 rounded-xl mt-1" data-testid="reschedule-date-input" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-slate-600">New start</Label>
              <Input type="time" value={form.new_start_time} onChange={(e) => setForm({ ...form, new_start_time: e.target.value })} className="h-10 rounded-xl mt-1" data-testid="reschedule-start-time" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">New end</Label>
              <Input type="time" value={form.new_end_time} onChange={(e) => setForm({ ...form, new_end_time: e.target.value })} className="h-10 rounded-xl mt-1" data-testid="reschedule-end-time" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-600 flex items-center gap-1"><Bell size={11} weight="fill" />Note to buyer (sent in the notification)</Label>
            <Input value={form.note_to_buyer} onChange={(e) => setForm({ ...form, note_to_buyer: e.target.value })} placeholder="e.g., Room A1 was double-booked; moved you to A2." className="h-10 rounded-xl mt-1" data-testid="reschedule-note" />
          </div>
          <label className="flex items-start gap-2 cursor-pointer text-xs text-slate-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
            <input type="checkbox" checked={form.override_capacity} onChange={(e) => setForm({ ...form, override_capacity: e.target.checked })} className="mt-0.5 accent-amber-500" data-testid="reschedule-override" />
            <span><Warning size={11} weight="fill" className="inline text-amber-600 mr-0.5" /> <strong>Override capacity</strong> — force the move even if the new slot is at capacity.</span>
          </label>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-600 h-9 rounded-lg text-xs">Cancel</Button>
          <Button size="sm" onClick={submit} disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white h-9 rounded-lg text-xs" data-testid="reschedule-submit">
            {busy ? 'Moving…' : 'Move & Notify'}
          </Button>
        </div>
      </div>
    </div>
  );
};

/* =================================================================================
   ManualBookingModal
================================================================================= */
const ManualBookingModal = ({ target, defaultDate, onClose, onDone }) => {
  const [mode, setMode] = useState('existing'); // 'existing' | 'walkin'
  const [lookupEmail, setLookupEmail] = useState('');
  const [lookupResult, setLookupResult] = useState(null); // null | {found, buyer?}
  const [lookupBusy, setLookupBusy] = useState(false);
  const [myListings, setMyListings] = useState([]);
  const [selectedListingId, setSelectedListingId] = useState(target.listing_id || '');
  const [form, setForm] = useState({
    date: defaultDate,
    start_time: target.slot?.start_time || '10:00',
    end_time: target.slot?.end_time || '11:00',
    guests: 1,
    payment_mode: 'paid_direct',
    payment_reference: '',
    notes: '',
    guest_name: '',
    guest_phone: '',
    guest_email: '',
    override_capacity: false,
  });
  const [busy, setBusy] = useState(false);

  // Fetch this seller's listings so the picker is populated even when the day is empty.
  useEffect(() => {
    if (selectedListingId) return; // already set
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/listings/my-listings`);
        const items = data?.listings || [];
        setMyListings(items);
        if (items.length && !selectedListingId) setSelectedListingId(items[0].listing_id);
      } catch (e) { console.error('Failed to load seller listings for picker:', e?.message); }
    })();
  }, [selectedListingId]);

  const doLookup = async () => {
    const email = lookupEmail.trim().toLowerCase();
    if (!email) { toast.error('Enter an email'); return; }
    setLookupBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/seller/slot-manager/lookup-buyer`, { email });
      setLookupResult(data);
      if (!data.found) toast.info('No registered buyer with that email — try Walk-in mode.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Lookup failed'); }
    finally { setLookupBusy(false); }
  };

  const submit = async () => {
    if (!selectedListingId) { toast.error('Pick a listing first'); return; }
    if (mode === 'existing' && !lookupResult?.found) { toast.error('Look up an existing buyer first, or switch to Walk-in.'); return; }
    if (mode === 'walkin' && !form.guest_name.trim() && !form.guest_phone.trim()) { toast.error('Enter at least the walk-in buyer\u2019s name or phone'); return; }
    setBusy(true);
    try {
      const payload = {
        listing_id: selectedListingId,
        start_date: `${form.date}T00:00:00Z`,
        end_date: `${form.date}T23:59:00Z`,
        start_time: form.start_time,
        end_time: form.end_time,
        guests: form.guests,
        payment_mode: form.payment_mode,
        payment_reference: form.payment_reference,
        notes: form.notes,
        override_capacity: form.override_capacity,
      };
      if (mode === 'existing') {
        payload.buyer_id = lookupResult.buyer.user_id;
      } else {
        payload.guest_name = form.guest_name;
        payload.guest_phone = form.guest_phone;
        payload.guest_email = form.guest_email;
      }
      const { data } = await axios.post(`${API_URL}/api/seller/slot-manager/manual-booking`, payload);
      toast.success(data.buyer_notified ? 'Booking added & buyer notified' : 'Walk-in booking added');
      onDone();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Manual booking failed';
      if (err.response?.status === 409 && !form.override_capacity) toast.error(msg + ' — tick "Override capacity" to force.');
      else toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="manual-booking-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md p-5 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <UserPlus size={18} className="text-emerald-600" weight="fill" />
            <h3 className="font-bold text-slate-900">Add Buyer to Slot</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><XIcon size={18} /></button>
        </div>
        <p className="text-xs text-slate-500 mb-3 truncate">→ <strong className="text-slate-700">{target.listing_title}</strong>{target.slot ? ` · ${target.slot.start_time}–${target.slot.end_time}` : ''}</p>

        {/* Listing picker — visible only when seller opened from the FAB without a slot context */}
        {!target.slot && myListings.length > 0 && (
          <div className="mb-3">
            <Label className="text-xs text-slate-600">Listing</Label>
            <Select value={selectedListingId} onValueChange={setSelectedListingId}>
              <SelectTrigger className="h-10 rounded-xl mt-1 text-sm" data-testid="manual-listing-select"><SelectValue placeholder="Pick a listing" /></SelectTrigger>
              <SelectContent>
                {myListings.map(l => (<SelectItem key={l.listing_id} value={l.listing_id}>{l.title}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* mode tabs */}
        <div className="grid grid-cols-2 gap-1 bg-slate-100 rounded-xl p-1 mb-3">
          {['existing', 'walkin'].map(m => (
            <button key={m} onClick={() => setMode(m)} className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${mode === m ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid={`mode-${m}`}
              data-testid-alt={m === 'existing' ? 'mode-registered' : undefined}>
              {m === 'existing' ? '👤 Registered buyer' : '🚶 Walk-in (offline)'}
            </button>
          ))}
        </div>

        {mode === 'existing' ? (
          <div className="space-y-3 mb-3">
            <div>
              <Label className="text-xs text-slate-600 flex items-center gap-1"><EnvelopeSimple size={11} weight="fill" /> Lookup by email</Label>
              <div className="flex gap-2 mt-1">
                <Input value={lookupEmail} onChange={(e) => setLookupEmail(e.target.value)} placeholder="buyer@email.com" className="h-10 rounded-xl flex-1" data-testid="lookup-email-input" />
                <Button size="sm" onClick={doLookup} disabled={lookupBusy} className="bg-slate-800 hover:bg-slate-900 h-10 rounded-xl text-xs" data-testid="lookup-btn">
                  <MagnifyingGlass size={14} weight="bold" className="mr-1" /> Find
                </Button>
              </div>
            </div>
            {lookupResult && lookupResult.found && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2" data-testid="lookup-result-found">
                <CheckCircle size={16} weight="fill" className="text-emerald-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{lookupResult.buyer.name}</p>
                  <p className="text-[11px] text-slate-500 truncate">{lookupResult.buyer.email} · {lookupResult.buyer.role}</p>
                </div>
              </div>
            )}
            {lookupResult && !lookupResult.found && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2 text-xs text-amber-800">
                <XCircle size={16} weight="fill" className="text-amber-600 shrink-0" />
                No registered buyer with that email. Try Walk-in mode above.
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-2 mb-3">
            <Input value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} placeholder="Walk-in name *" className="h-10 rounded-xl" data-testid="walkin-name" />
            <div className="grid grid-cols-2 gap-2">
              <Input value={form.guest_phone} onChange={(e) => setForm({ ...form, guest_phone: e.target.value })} placeholder="Phone *" className="h-10 rounded-xl" data-testid="walkin-phone" />
              <Input value={form.guest_email} onChange={(e) => setForm({ ...form, guest_email: e.target.value })} placeholder="Email (optional)" className="h-10 rounded-xl" data-testid="walkin-email" />
            </div>
          </div>
        )}

        {/* common slot + payment fields */}
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-3">
              <Label className="text-xs text-slate-600">Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-10 rounded-xl mt-1" data-testid="manual-date" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Start</Label>
              <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="h-10 rounded-xl mt-1" data-testid="manual-start-time" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">End</Label>
              <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="h-10 rounded-xl mt-1" data-testid="manual-end-time" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Guests</Label>
              <Input type="number" min={1} value={form.guests} onChange={(e) => setForm({ ...form, guests: parseInt(e.target.value) || 1 })} className="h-10 rounded-xl mt-1" data-testid="manual-guests" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Payment</Label>
            <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
              <SelectTrigger className="h-10 rounded-xl mt-1 text-sm" data-testid="manual-payment-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="paid_direct">💵 Mark as paid (cash/offline)</SelectItem>
                <SelectItem value="pending_link">📧 Send payment link / pending</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.payment_mode === 'paid_direct' && (
            <Input value={form.payment_reference} onChange={(e) => setForm({ ...form, payment_reference: e.target.value })} placeholder="Payment reference (optional)" className="h-10 rounded-xl" data-testid="manual-pay-ref" />
          )}
          <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes (optional)" className="h-10 rounded-xl" data-testid="manual-notes" />
          <label className="flex items-start gap-2 cursor-pointer text-xs text-slate-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
            <input type="checkbox" checked={form.override_capacity} onChange={(e) => setForm({ ...form, override_capacity: e.target.checked })} className="mt-0.5 accent-amber-500" data-testid="manual-override" />
            <span><Warning size={11} weight="fill" className="inline text-amber-600 mr-0.5" /> <strong>Override capacity</strong> if the slot is already full.</span>
          </label>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-600 h-9 rounded-lg text-xs">Cancel</Button>
          <Button size="sm" onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 rounded-lg text-xs" data-testid="manual-submit">
            {busy ? 'Adding…' : 'Add Booking'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SellerSlotManager;
